import React, { useState, useEffect } from 'react';
import { settingsCompanyApi } from '../../services/api';
import type { CompanySettingsDto } from '../../services/api';

type GeneralTab = 'company' | 'modules' | 'billing' | 'users';

const TABS: { id: GeneralTab; label: string; icon: React.ReactNode }[] = [
  {
    id: 'company',
    label: 'Company',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    id: 'modules',
    label: 'Modules',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'users',
    label: 'Users',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
];

const INDUSTRIES = ['General Business', 'Retail', 'Manufacturing', 'Services', 'Technology', 'Healthcare', 'Construction', 'Other'];
const CURRENCIES = ['USD - US Dollar', 'EUR - Euro', 'GBP - British Pound', 'PHP - Philippine Peso', 'JPY - Japanese Yen', 'Other'];
const COUNTRIES = ['United States', 'United Kingdom', 'Philippines', 'Canada', 'Australia', 'Germany', 'Other'];

export default function GeneralPage() {
  const [activeTab, setActiveTab] = useState<GeneralTab>('company');
  const [company, setCompany] = useState<CompanySettingsDto | null>(null);
  const [companyForm, setCompanyForm] = useState<CompanySettingsDto>({
    company_name: '',
    trading_name: '',
    industry: '',
    currency: '',
    tax_id: '',
    registration_number: '',
    email: '',
    phone: '',
    website: '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [modulesEnabled, setModulesEnabled] = useState<Record<string, boolean>>({
    accounting: true,
    sales: true,
    purchases: true,
    inventory: true,
    projects: true,
    hr: true,
  });

  useEffect(() => {
    let cancelled = false;
    settingsCompanyApi.get().then((res) => {
      if (cancelled || !res.data) return;
      setCompany(res.data);
      setCompanyForm({
        company_name: res.data.company_name ?? '',
        trading_name: res.data.trading_name ?? '',
        industry: res.data.industry ?? '',
        currency: res.data.currency ?? '',
        tax_id: res.data.tax_id ?? '',
        registration_number: res.data.registration_number ?? '',
        email: res.data.email ?? '',
        phone: res.data.phone ?? '',
        website: res.data.website ?? '',
        street_address: res.data.street_address ?? '',
        city: res.data.city ?? '',
        state_province: res.data.state_province ?? '',
        postal_code: res.data.postal_code ?? '',
        country: res.data.country ?? '',
      });
    });
    return () => { cancelled = true; };
  }, []);

  const handleCompanyChange = (field: keyof CompanySettingsDto, value: string) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
    setSaveMessage(null);
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    setSaveMessage(null);
    const res = await settingsCompanyApi.update(companyForm);
    setSaving(false);
    if (res.data) {
      setCompany(res.data);
      setSaveMessage({ type: 'success', text: res.message || 'Company settings saved.' });
    } else {
      setSaveMessage({ type: 'error', text: res.message || 'Failed to save.' });
    }
  };

  const toggleModule = (key: string) => {
    setModulesEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <h1 className="text-lg font-bold text-gray-900 mb-1 shrink-0">Settings</h1>
      <p className="text-sm text-gray-500 mb-4 shrink-0">
        Configure your company and application settings.
      </p>

      <nav className="flex gap-1 border-b border-gray-200 mb-4 shrink-0" aria-label="General settings tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-brand-600 text-brand-700 bg-gray-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="bg-white rounded-xl shadow-card border border-gray-100 flex-1 min-h-0 overflow-auto">
        {activeTab === 'company' && (
          <div className="p-6 space-y-8">
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Company Information</h2>
              <p className="text-sm text-gray-500 mb-4">Basic information about your business.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={companyForm.company_name}
                    onChange={(e) => handleCompanyChange('company_name', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Legal company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trading Name</label>
                  <input
                    type="text"
                    value={companyForm.trading_name}
                    onChange={(e) => handleCompanyChange('trading_name', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Display name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <select
                    value={companyForm.industry}
                    onChange={(e) => handleCompanyChange('industry', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">Select industry</option>
                    {INDUSTRIES.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={companyForm.currency}
                    onChange={(e) => handleCompanyChange('currency', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">Select currency</option>
                    {CURRENCIES.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    value={companyForm.tax_id}
                    onChange={(e) => handleCompanyChange('tax_id', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Tax identification"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                  <input
                    type="text"
                    value={companyForm.registration_number}
                    onChange={(e) => handleCompanyChange('registration_number', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Business registration #"
                  />
                </div>
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={companyForm.email}
                      onChange={(e) => handleCompanyChange('email', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={companyForm.phone}
                      onChange={(e) => handleCompanyChange('phone', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={companyForm.website}
                    onChange={(e) => handleCompanyChange('website', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="https://company.com"
                  />
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Address</h2>
              <p className="text-sm text-gray-500 mb-4">Your business address.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={companyForm.street_address}
                    onChange={(e) => handleCompanyChange('street_address', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={companyForm.city}
                    onChange={(e) => handleCompanyChange('city', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={companyForm.state_province}
                    onChange={(e) => handleCompanyChange('state_province', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="State / Province"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={companyForm.postal_code}
                    onChange={(e) => handleCompanyChange('postal_code', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Postal code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    value={companyForm.country}
                    onChange={(e) => handleCompanyChange('country', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleSaveCompany}
                disabled={saving || !companyForm.company_name.trim()}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              {saveMessage && (
                <span className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {saveMessage.text}
                </span>
              )}
            </div>
          </div>
        )}

        {activeTab === 'modules' && (
          <div className="p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Application Modules</h2>
            <p className="text-sm text-gray-500 mb-6">Enable or disable modules for your organization.</p>
            <ul className="space-y-3">
              {[
                { key: 'accounting', label: 'Accounting', desc: 'Chart of accounts, journals, general ledger, bank & cash' },
                { key: 'sales', label: 'Sales & CRM', desc: 'Customers, invoices, payments received' },
                { key: 'purchases', label: 'Purchases', desc: 'Vendors, bills, payments made' },
                { key: 'inventory', label: 'Inventory', desc: 'Products and stock' },
                { key: 'projects', label: 'Projects', desc: 'Planning, team, task tracker, monitoring' },
                { key: 'hr', label: 'HR & Payroll', desc: 'Employees, attendance, payroll' },
              ].map(({ key, label, desc }) => (
                <li key={key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{label}</p>
                    <p className="text-sm text-gray-500">{desc}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={modulesEnabled[key]}
                    onClick={() => toggleModule(key)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                      modulesEnabled[key] ? 'bg-brand-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                        modulesEnabled[key] ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-500 mt-4">Module visibility can be wired to your backend; changes here are stored in local state.</p>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Billing & Subscription</h2>
            <p className="text-sm text-gray-500 mb-6">Manage your subscription and payment methods.</p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
              <svg className="mx-auto w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <p className="mt-2 font-medium text-gray-700">Current plan</p>
              <p className="text-sm text-gray-500 mt-1">Connect a billing provider to show plan and usage here.</p>
              <button
                type="button"
                className="mt-4 inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Manage billing
              </button>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Users</h2>
            <p className="text-sm text-gray-500 mb-6">Manage user accounts and access. Use Access Control for roles and permissions.</p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
              <svg className="mx-auto w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="mt-2 font-medium text-gray-700">User management</p>
              <p className="text-sm text-gray-500 mt-1">List and invite users; assign roles in Settings → Access Control.</p>
              <button
                type="button"
                className="mt-4 inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Invite user
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
